import type { Embedding, ThemeEmbeddings } from './search.types';
import type { PrecomputedEmbeddingsBundle } from './precomputedEmbeddingsBundle.types';

/**
 * The theme-search index, shared by the browser and the SSR server (PRNT-5582).
 *
 * Pure and dependency-free on purpose: the server renderer builds the same themes map the client
 * builds, so a server-rendered `style:*` gallery URL and its hydration agree on which designs
 * match. Keep `fetch`, React and app imports out of this module — both sides import it.
 */

export const EMBEDDINGS_STORE = 'https://withjoy.com/media/raw/paper-embeddings';

/**
 * Color keywords in the bundle are namespaced (`color: roseGold`) to keep them
 * apart from style keywords. Returns the bare color name, or `''` for a keyword
 * that isn't a color (a theme's color slot may hold `colorful`).
 */
const COLOR_KEYWORD_PREFIX = 'color: ';
export function colorKeywordName(keyword: string | undefined): string {
  return keyword?.startsWith(COLOR_KEYWORD_PREFIX) ? keyword.slice(COLOR_KEYWORD_PREFIX.length) : '';
}

/**
 * Builds the themes map from the bundle, with the keyword vectors when the caller has them.
 *
 * Omit `embeddings` for a names-only map: every vector list comes back empty while `conceptNames`
 * and `colorNames` are fully populated. That is what the server renders from — `styleOptionsForTemplate`
 * and `useColorHits` read only the names — and it is why SSR never has to fetch `themes.bin`.
 * A consumer that scores by similarity must wait for the vectors; an empty `concepts` scores 0,
 * which is indistinguishable from "no match".
 */
export function buildThemeEmbeddings(bundle: PrecomputedEmbeddingsBundle, embeddings?: ReadonlyArray<Embedding>): Map<string, ThemeEmbeddings> {
  const vectorsAt = (indexes: ReadonlyArray<number>): ReadonlyArray<Embedding> => (embeddings ? indexes.map(i => embeddings[i]) : []);

  return new Map(
    // Object.entries is >95% the compat table is being updated soon.
    // eslint-disable-next-line compat/compat
    Object.entries(bundle.themes).map(([id, [conceptIndexes, colorIndexes, customIndexes]]): [string, ThemeEmbeddings] => {
      return [
        id,
        {
          concepts: vectorsAt(conceptIndexes),
          conceptNames: conceptIndexes.map(i => bundle.keywords[i]),
          colors: embeddings && colorIndexes.length > 0 ? (colorIndexes.map(i => embeddings[i]) as Readonly<[Embedding, Embedding, Embedding]> | []) : ([] as []),
          colorNames: colorIndexes.map(i => colorKeywordName(bundle.keywords[i])),
          customs: vectorsAt(customIndexes),
        },
      ];
    }),
  );
}
